import React, { Fragment } from "react"

import { useTranslation } from "react-i18next"

import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace"
import { CircularProgress, Grid } from "@mui/material"
import { Button, MenuItem, Tag, Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { StatusColorMap, StatusTextMap } from "../../features/Scripts/Interfaces"
import type { ScriptVersion } from "../../types/generated/api/ScriptVersion"
import { KonanTimeHelper, isToday } from "../../utils/genericHelpers"
import { KonanAvatarComponent } from "../core/KonanAvatar"

import styles from "./VersionHistory.module.scss"

interface RenderVersionsProps {
  versions: Array<ScriptVersion>
  selectedVersion: string
  setSelectedVersion: React.Dispatch<React.SetStateAction<string | undefined>>
  lastVersionRef?: (node?: Element | null) => void
  hasNextPage?: boolean
}

const RenderVersions = (props: RenderVersionsProps): React.ReactElement => {
  const { versions, selectedVersion, setSelectedVersion, lastVersionRef, hasNextPage } = props

  const konanTime = new KonanTimeHelper()

  return (
    <Fragment>
      {versions.map((version, index: number) => {
        const sameDateAsBefore = konanTime.datesAreOnSameDay(
          new Date(version?.created_at),
          new Date(versions[index - 1]?.created_at ?? ""),
        )

        return (
          <VersionCard
            key={version?.version}
            version={version}
            selectedVersion={selectedVersion}
            setSelectedVersion={setSelectedVersion}
            sameDateAsBefore={sameDateAsBefore}
            index={index}
            lastVersionRef={
              hasNextPage && versions.length !== 0 && index === versions.length - 1 ? lastVersionRef : undefined
            }
          />
        )
      })}
    </Fragment>
  )
}

interface VersionCardProps {
  title?: string
  version: ScriptVersion
  selectedVersion?: string
  setSelectedVersion?: React.Dispatch<React.SetStateAction<string | undefined>>
  sameDateAsBefore?: boolean
  index?: number
  lastVersionRef?: ((node?: Element | null) => void) | undefined
}

const VersionCard = (props: VersionCardProps): React.ReactElement => {
  const { title, version, selectedVersion, setSelectedVersion, sameDateAsBefore, index, lastVersionRef } = props

  const { t } = useTranslation("UI")

  return (
    <Fragment>
      {!sameDateAsBefore && (
        <Typography
          variant="label-regular"
          gutterBottom
          variantColor={2}
          style={{ marginTop: (index as number) > 0 ? "16px" : "0px" }}
        >
          {title
            ? title
            : isToday(version?.created_at)
              ? t("VersionHistory.today")
              : moment(version?.created_at).format("ddd, Do MMMM, yyyy")}
        </Typography>
      )}

      <MenuItem
        ref={lastVersionRef}
        key={version?.version}
        isSelected={version?.version === selectedVersion}
        // this to avoid fetching the same version we already standing on
        onClick={() => version?.version !== selectedVersion && setSelectedVersion?.(version?.version)}
        style={{ marginLeft: "0px" }}
      >
        <Grid item container justifyContent="flex-start" wrap="nowrap" gap={1}>
          <Grid item xs={2} display={"flex"} alignItems={"center"}>
            <Typography noWrap variant={"a"} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              v{version?.version}
            </Typography>
          </Grid>

          <Grid item xs minWidth={0}>
            <KonanAvatarComponent
              createdBy={version.created_by ?? "Synapse-bot"}
              createdAt={version.created_at}
              title={version.created_by?.split("@")[0] ?? "Synapse-bot"}
              avatarSize={24}
            />
          </Grid>

          {(version as ScriptVersion & { is_deprecated: boolean })?.is_deprecated ? (
            <Grid item xs display={"flex"} alignItems={"center"} justifySelf={"flex-end"} justifyContent={"flex-end"}>
              <Tag size="small" color="red">
                {t("VersionHistory.deprecated")}
              </Tag>
            </Grid>
          ) : version.is_active_version ? (
            <Grid item xs justifySelf={"flex-end"} justifyContent={"flex-end"} display={"flex"} alignItems={"center"}>
              <Tag size={"small"} color="green">
                {t("VersionHistory.active")}
              </Tag>
            </Grid>
          ) : (
            (version.status as keyof typeof StatusTextMap) && (
              <Grid item xs justifySelf={"flex-end"} justifyContent={"flex-end"} display={"flex"} alignItems={"center"}>
                <Tag size={"small"} color={StatusColorMap[version.status as keyof typeof StatusColorMap]}>
                  {t(`types.StatusTextMap.${StatusTextMap[version.status as keyof typeof StatusTextMap]}`, {
                    ns: "scripts",
                  })}
                </Tag>
              </Grid>
            )
          )}
        </Grid>
      </MenuItem>
    </Fragment>
  )
}

interface VersionHistoryProps {
  versions: ScriptVersion[]
  selectedVersion: string
  setSelectedVersion: React.Dispatch<React.SetStateAction<string | undefined>>
  onClose?: () => void
  isLoading: boolean
  activeVersion: ScriptVersion
  height?: number

  // This is just a flag to indicate whether this version history will follow 100% the new revamp layout or not
  // TODO: remove it after revamp
  isScriptView?: boolean
  totalCount?: number

  infiniteScrollProps?: {
    hasNextPage?: boolean
    isFetchingNextPage: boolean
    lastVersionRef: (node?: Element | null) => void
  }
}

export function VersionHistory(props: Readonly<VersionHistoryProps>): React.ReactElement {
  const {
    onClose,
    height,
    setSelectedVersion,
    selectedVersion,
    versions,
    isLoading,
    activeVersion,
    isScriptView,
    infiniteScrollProps,
  } = props

  const { t } = useTranslation("UI")

  return (
    <Grid item px={2} pt={2} className={styles.root} id="version-history">
      {/* Header */}
      <Grid item xs={12} display={"flex"} gap={1.5}>
        {onClose && (
          <Grid item>
            <Button onClick={() => onClose?.()} size="small">
              <KeyboardBackspaceIcon fontSize="small" />
            </Button>
          </Grid>
        )}

        {isScriptView && (
          <Grid item>
            <Typography variant="h2-bold" textTransform="capitalize">
              {t("VersionHistory.title")}
            </Typography>
          </Grid>
        )}
      </Grid>

      {/* Body */}
      <Grid item xs={12} py={3} height={height} className={height ? styles.versionsList : ""}>
        <VersionCard
          setSelectedVersion={setSelectedVersion}
          title={t("VersionHistory.activeVersion")}
          version={activeVersion}
          key={"active-version"}
        />

        {/* isLoading -> for initial loading of data */}
        {isLoading && (
          <Grid item mt={3} display={"flex"} alignItems={"center"} justifyContent={"center"}>
            <CircularProgress size={20} color="primary" />
          </Grid>
        )}

        {/* default case -> all versions at one */}
        {!infiniteScrollProps && (
          <RenderVersions
            selectedVersion={selectedVersion}
            setSelectedVersion={setSelectedVersion}
            versions={versions}
          />
        )}

        {/* infinite scrolling case -> has to be given infiniteScrollProps */}
        {infiniteScrollProps && (
          <Fragment>
            <RenderVersions
              selectedVersion={selectedVersion}
              setSelectedVersion={setSelectedVersion}
              versions={versions}
              lastVersionRef={infiniteScrollProps.lastVersionRef}
              hasNextPage={infiniteScrollProps.hasNextPage}
            />
            {infiniteScrollProps.isFetchingNextPage && (
              <Grid item container direction="column" justifyContent="center" alignItems="center">
                <CircularProgress size={20} />
              </Grid>
            )}
          </Fragment>
        )}
      </Grid>
    </Grid>
  )
}
